import { Form, Input, Select, Switch } from 'antd';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import {
  useBranches,
  useRoles,
  useUsers,
  usersKey,
  type RoleAssignment,
  type User,
} from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { handleEnterKeyNavigation } from '@/components/enterKeyNavigation';
import { FormDrawer } from '@/components/FormDrawer';
import { IconAction } from '@/components/IconAction';
import { OneTimePasswordModal, type IssuedPassword } from '@/components/OneTimePasswordModal';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { resetUserPassword } from '@core/api/userCommands';
import { formatDateTime } from '@core/format/dates';
import { blankToNull } from '@core/format/text';
import { canWrite } from '@core/session/capability';
import type { Session } from '@core/session/types';

interface CreatedUser {
  user: User;
  temporaryPassword: string;
}

type Editing = { mode: 'create' } | { mode: 'edit'; user: User };

/**
 * User administration and branch-role assignment (spec Section 4).
 *
 * Three things here are not ordinary CRUD. An administrator never chooses a password — the server
 * generates one, returns it exactly once and forces a change at first sign-in — so the screen has
 * to treat that value as something to hand over now or lose. Roles are held *per branch*, so the
 * assignment list is a table rather than a multi-select. And the account that owns the tenant is
 * protected from being locked out of its own system: the server refuses to deactivate, delete, or
 * strip the last administrator role from it, and the buttons say so rather than finding out.
 */
export function UsersPage({ session }: { session: Session }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [assigningTo, setAssigningTo] = useState<number | null>(null);
  const [issued, setIssued] = useState<IssuedPassword | null>(null);

  const users = useUsers(includeInactive);

  // Read back from the list rather than held in state, so the drawer redraws from the server's
  // answer after every assignment instead of from a copy that is one write out of date.
  const assigning = users.data?.find((user) => user.id === assigningTo);

  const remove = useApiMutation<void, User>({
    mutationFn: (user) => apiFetch<void>(`/api/user/${user.id}`, { method: 'DELETE' }),
    success: (_result, user) => `${user.fullName} deleted.`,
    invalidates: [usersKey],
  });

  const resetPassword = useApiMutation<{ temporaryPassword: string }, User>({
    mutationFn: (user) => resetUserPassword(user.id),
    invalidates: [usersKey],
    onSuccess: (result, user) =>
      setIssued({ username: user.username, password: result.temporaryPassword }),
  });

  const columns: DataViewColumn<User>[] = [
    {
      title: 'Name',
      dataIndex: 'fullName',
      // The card heading. It already stacks the name, the owner badge and the email, which is
      // exactly what a heading wants — nothing to rearrange.
      primary: true,
      render: (_, row) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{row.fullName}</span>
            {row.isTenantOwner && <StatusBadge tone="brand">Owner</StatusBadge>}
          </div>
          <p className="text-[12px] text-default">{row.email ?? '—'}</p>
        </div>
      ),
    },
    {
      title: 'Username',
      dataIndex: 'username',
      width: 150,
      // Mono for both identifiers: they are values to be read back and typed exactly, not prose.
      render: (_, row) => <span className="font-mono-nums font-semibold">{row.username}</span>,
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      width: 140,
      render: (_, row) => <span className="font-mono-nums">{row.mobile}</span>,
    },
    {
      title: 'Roles',
      key: 'roles',
      width: 280,
      render: (_, row) =>
        row.roles.length === 0 ? (
          // Not a neutral state: a user with no role can sign in and do nothing at all.
          <StatusBadge tone="warning">No roles</StatusBadge>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.roles.map((assignment) => (
              <span
                key={assignment.id}
                className="inline-flex items-center gap-1 rounded border border-border-color bg-light px-1.5 py-0.5 text-[11px]"
              >
                {assignment.roleName}
                <span className="text-default">{assignment.branchCode ?? 'all branches'}</span>
              </span>
            ))}
          </div>
        ),
    },
    {
      title: 'Last sign-in',
      dataIndex: 'lastLoginOn',
      width: 150,
      responsive: ['xl'],
      render: (_, row) =>
        row.lastLoginOn ? (
          <span className="font-mono-nums text-[12px]">{formatDateTime(row.lastLoginOn)}</span>
        ) : (
          <span className="text-default">Never</span>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {row.isActive ? (
            <StatusBadge tone="positive">Active</StatusBadge>
          ) : (
            <StatusBadge tone="neutral">Inactive</StatusBadge>
          )}
          {row.isLockedOut && <StatusBadge tone="negative">Locked out</StatusBadge>}
          {row.mustChangePassword && <StatusBadge tone="warning">New password</StatusBadge>}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      trailing: true,
      align: 'right',
      fixed: 'right',
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <WriteAction session={session} required="User:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.fullName}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', user: row })}
              />
            )}
          </WriteAction>

          <WriteAction session={session} required="User:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-shield-check"
                label={`Roles for ${row.fullName}`}
                disabled={disabled}
                onClick={() => setAssigningTo(row.id)}
              />
            )}
          </WriteAction>

          <WriteAction session={session} required="User:Edit">
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-key"
                label={`Reset password for ${row.fullName}`}
                title="Issue a new password?"
                description="Their current password stops working immediately and every session ends."
                confirmText="Reset"
                disabled={disabled}
                pending={resetPassword.isPending}
                onConfirm={() => void resetPassword.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </WriteAction>

          <RecordAction
            session={session}
            required="User:Delete"
            forbidden={
              row.isTenantOwner
                ? 'The account that owns this company cannot be deleted'
                : row.id === session.user.id
                  ? 'You cannot delete your own account'
                  : undefined
            }
          >
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.fullName}`}
                title="Delete this user?"
                description="They are retained for audit, not erased, but they can no longer sign in."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  if (users.isError) {
    return <ApiErrorAlert error={users.error} />;
  }

  return (
    <>
      <PageHeading
        title="Users"
        subtitle="Who may sign in, and what they may do at which branch."
        actions={
          <>
            <Button
              icon={includeInactive ? 'icon icon-eye-off' : 'icon icon-eye'}
              onClick={() => setIncludeInactive((on) => !on)}
            >
              {includeInactive ? 'Hide inactive' : 'Show inactive'}
            </Button>

            <WriteAction session={session} required="User:Create">
              {(disabled) => (
                <Button
                  variant="primary"
                  icon="icon icon-plus"
                  shortcut="create"
                  disabled={disabled}
                  onClick={() => setEditing({ mode: 'create' })}
                >
                  New user
                </Button>
              )}
            </WriteAction>
          </>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <DataView<User>
          columns={columns}
          dataSource={users.data ?? []}
          loading={users.isLoading}
          empty={
            <EmptyState
              icon="icon icon-users"
              title="No users yet"
              hint="Roles decide what a job may do; users are the people who hold them."
            />
          }
        />
      </Card>

      {editing && (
        <UserDrawer
          editing={editing}
          onClose={() => setEditing(null)}
          onCreated={(created) =>
            setIssued({ username: created.user.username, password: created.temporaryPassword })
          }
        />
      )}

      {assigning && (
        <AssignmentsDrawer
          user={assigning}
          session={session}
          onClose={() => setAssigningTo(null)}
        />
      )}

      <OneTimePasswordModal issued={issued} onClose={() => setIssued(null)} />
    </>
  );
}

interface UserFormValues {
  username: string;
  mobile: string;
  email?: string | null;
  fullName: string;
  isActive: boolean;
}

/**
 * Create and edit.
 *
 * Username is create-only, because the login audit records it against every attempt — changing it
 * would rewrite what trail entries already written mean. The server's update contract does not
 * accept one either, so offering the field would be offering an edit that silently does nothing.
 *
 * The mobile number *is* editable, since people change numbers, and it is one of the two ways they
 * get in. Email is neither: it is contact information, and optional.
 */
function UserDrawer({
  editing,
  onClose,
  onCreated,
}: {
  editing: Editing;
  onClose: () => void;
  onCreated: (created: CreatedUser) => void;
}) {
  const [form] = Form.useForm<UserFormValues>();
  const isEdit = editing.mode === 'edit';
  const existing = editing.mode === 'edit' ? editing.user : undefined;

  const save = useApiMutation<User | CreatedUser, UserFormValues>({
    mutationFn: (raw) => {
      const values = blankToNull(raw);

      return isEdit
        ? apiFetch<User>(`/api/user/${existing?.id}`, {
            method: 'PUT',
            body: {
              fullName: values.fullName,
              mobile: values.mobile,
              email: values.email,
              isActive: values.isActive,
            },
          })
        : apiFetch<CreatedUser>('/api/user', {
            method: 'POST',
            body: {
              username: values.username,
              mobile: values.mobile,
              fullName: values.fullName,
              email: values.email,
            },
          });
    },
    // No toast on creation: the one-time password modal that follows is the confirmation, and a
    // message sliding over it would be noise on top of the one thing that must be read.
    ...(isEdit ? { success: 'User updated.' } : {}),
    invalidates: [usersKey],
    onSuccess: (result) => {
      onClose();

      if (!isEdit) {
        onCreated(result as CreatedUser);
      }
    },
  });

  return (
    <FormDrawer<UserFormValues>
      title={isEdit ? `Edit ${existing?.fullName ?? 'user'}` : 'New user'}
      open
      onClose={onClose}
      form={form}
      initialValues={existing ?? { username: '', mobile: '', fullName: '', isActive: true }}
      onSubmit={(values) => void submitVia(save, values)}
      submitText={isEdit ? 'Save changes' : 'Create user'}
      submitting={save.isPending}
      width={480}
    >
      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item
          name="username"
          label="Username"
          extra={
            isEdit
              ? 'Fixed at creation — the sign-in trail records it against every attempt'
              : 'Must start with a letter, so it can never be mistaken for a mobile number.'
          }
          rules={[
            { required: true, message: 'A username is required' },
            {
              pattern: /^[A-Za-z][A-Za-z0-9._-]{2,63}$/,
              message: 'Start with a letter; 3 to 64 letters, digits, dot, underscore or hyphen',
            },
          ]}
        >
          <Input disabled={isEdit} maxLength={64} className="font-mono-nums" />
        </Form.Item>

        <Form.Item
          name="mobile"
          label="Mobile"
          extra="The other way to sign in. Stored as digits, so it is one number however it is typed."
          rules={[
            { required: true, message: 'A mobile number is required' },
            {
              // Deliberately lenient about how it is written — the server normalises +91,
              // spaces and a leading zero away. This only insists there are enough digits.
              validator: (_rule, value: string | undefined) => {
                const digits = (value ?? '').replace(/\D/g, '');

                return digits.length >= 10 && digits.length <= 15
                  ? Promise.resolve()
                  : Promise.reject(new Error('Enter a mobile number'));
              },
            },
          ]}
        >
          <Input maxLength={20} className="font-mono-nums" />
        </Form.Item>
      </div>

      <Form.Item
        name="fullName"
        label="Full name"
        rules={[{ required: true, message: 'A name is required' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        extra="Contact only, and optional — nobody signs in with it."
        rules={[{ type: 'email', message: 'Not a valid email address' }]}
      >
        <Input />
      </Form.Item>

      {isEdit && (
        <Form.Item
          name="isActive"
          label="Active"
          valuePropName="checked"
          extra={
            existing?.isTenantOwner
              ? 'The account that owns this company cannot be deactivated'
              : 'An inactive user keeps their history but cannot sign in.'
          }
        >
          <Switch size="small" disabled={existing?.isTenantOwner ?? false} />
        </Form.Item>
      )}

      {!isEdit && (
        <Notice title="You do not choose their password">
          One is generated and shown once, on the next screen. They must change it at first sign-in,
          so the password you hand over is only good for that.
        </Notice>
      )}
    </FormDrawer>
  );
}

/**
 * Roles held, per branch.
 *
 * A role with no branch covers every branch *including ones created later*, which is the reason it
 * is offered as an explicit choice rather than inferred from an empty selection — "all branches"
 * is a decision about the future, not an omission.
 */
function AssignmentsDrawer({
  user,
  session,
  onClose,
}: {
  user: User;
  session: Session;
  onClose: () => void;
}) {
  const [form] = Form.useForm<{ roleId: number; branchId?: number }>();
  const roles = useRoles();
  const branches = useBranches();
  const writable = canWrite(session);

  const assign = useApiMutation<User, { roleId: number; branchId?: number }>({
    mutationFn: (body) => apiFetch<User>(`/api/user/${user.id}/role`, { method: 'POST', body }),
    success: 'Role granted.',
    invalidates: [usersKey],
    onSuccess: () => form.resetFields(['roleId']),
  });

  const revoke = useApiMutation<User, RoleAssignment>({
    mutationFn: (assignment) =>
      apiFetch<User>(`/api/user/${user.id}/role/${assignment.id}`, { method: 'DELETE' }),
    success: 'Role removed.',
    invalidates: [usersKey],
  });

  const columns: DataViewColumn<RoleAssignment>[] = [
    {
      title: 'Role',
      dataIndex: 'roleName',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="font-medium text-gray-900">{row.roleName}</div>
          <span className="font-mono-nums text-[11px] text-default">{row.roleCode}</span>
        </div>
      ),
    },
    {
      title: 'Applies at',
      key: 'branch',
      width: 200,
      render: (_, row) =>
        row.branchId === null ? (
          <StatusBadge tone="brand">All branches</StatusBadge>
        ) : (
          <span>
            {row.branchName}{' '}
            <span className="font-mono-nums text-[11px] text-default">{row.branchCode}</span>
          </span>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      trailing: true,
      align: 'right',
      render: (_, row) => (
        <ConfirmButton
          icon="icon icon-trash-2"
          label={`Remove ${row.roleName}`}
          title="Remove this role?"
          description="They lose whatever it granted, at that branch, immediately."
          confirmText="Remove"
          disabled={!writable}
          pending={revoke.isPending}
          onConfirm={() => void revoke.mutateAsync(row).catch(() => undefined)}
        />
      ),
    },
  ];

  return (
    <div className="fixed inset-0 z-9999 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[var(--overlay-scrim)]" aria-hidden="true" />

      <div className="relative flex h-full w-full max-w-3xl flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg">
        <div className="flex items-center justify-between gap-4 border-b border-border-color p-4">
          <h2 className="text-base font-bold text-gray-900">Roles — {user.fullName}</h2>

          <button
            type="button"
            className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border-color hover:bg-light"
            aria-label="Close"
            onClick={onClose}
          >
            <i className="icon icon-x text-sm" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {user.isTenantOwner && (
            <Notice title="This account owns the company">
              Its last administrator role cannot be removed — otherwise nobody would be left who
              could grant it back.
            </Notice>
          )}

          <Card flush className="p-3 pb-0">
            {/* This one lives inside a drawer, which is narrow on every device — so the card
                layout is not a mobile concession here, it is the common case. */}
            <DataView<RoleAssignment>
              columns={columns}
              dataSource={user.roles}
              empty={
                <EmptyState
                  icon="icon icon-shield-check"
                  title="No roles yet"
                  hint="This user can sign in and do nothing until they hold one."
                />
              }
            />
          </Card>

          <div>
            <h3 className="mb-2 text-sm font-bold text-gray-900">Grant another role</h3>

            <Form
              form={form}
              layout="vertical"
              disabled={!writable}
              requiredMark={false}
              initialValues={{ branchId: 0 }}
              // Role then branch then Grant, without leaving the keyboard — see
              // `enterKeyNavigation`. This form sits inside a drawer but not inside its `<form>`,
              // so it needs the handler of its own.
              onKeyDown={handleEnterKeyNavigation}
              onFinish={(values) =>
                void submitVia(assign, {
                  roleId: values.roleId,
                  // 0 is the sentinel for "every branch" in the select below; the API wants the
                  // field absent, and sending 0 would be a branch id that does not exist.
                  ...(values.branchId ? { branchId: values.branchId } : {}),
                })
              }
            >
              <div className="flex flex-wrap items-end gap-3">
                <Form.Item
                  name="roleId"
                  label="Role"
                  className="mb-0 min-w-52"
                  rules={[{ required: true, message: 'Choose a role' }]}
                >
                  <Select
                    loading={roles.isLoading}
                    options={(roles.data ?? []).map((role) => ({
                      label: role.name,
                      value: role.id,
                    }))}
                  />
                </Form.Item>

                <Form.Item name="branchId" label="At" className="mb-0 min-w-52">
                  <Select
                    loading={branches.isLoading}
                    options={[
                      { label: 'All branches', value: 0 },
                      ...(branches.data ?? []).map((branch) => ({
                        label: `${branch.name} (${branch.code})`,
                        value: branch.id,
                      })),
                    ]}
                  />
                </Form.Item>

                <Button variant="primary" type="submit" disabled={!writable || assign.isPending}>
                  {assign.isPending ? 'Granting…' : 'Grant'}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
